import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";
import { NotifierService } from "src/app/_services/notifier.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-ec2-dashboard",
  templateUrl: "./ec2-dashboard.component.html",
  styleUrls: ["../../launch-ec2.operations.component.css"],
})
export class Ec2DashboardComponent implements OnInit, OnDestroy {
  @Input() preserveServiceCatalogHeader = false;

  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;
  writeAccess: boolean =
    localStorage.getItem("acT") == "readandwrite" ||
    localStorage.getItem("acT") == "readOnly(startstop)" ||
    this.urlPrefix == "admin" ||
    localStorage.getItem("ut") == "admin";

  loading: boolean = false;
  error: string = "";
  rows: any[] = [];
  accountId: string = "";
  region: string = "";
  currentMessage: Subscription;
  showLaunchEc2Modal: boolean = false;
  private loadSequence: number = 0;
  private contextRefreshTimer: any = null;
  private destroyed: boolean = false;

  constructor(private apiServer: APIService, private notifier: NotifierService) {}

  ngOnInit(): void {
    this.accountId = this.normalizeContextValue(localStorage.getItem("accountId"));
    this.region = this.normalizeContextValue(localStorage.getItem("regionId"));

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) return;
      try {
        const d = JSON.parse(msg);
        if (d.value == null || d.source != "notifier") return;
        if (d.key == "accountId" || d.key == "regionId") {
          this.applyContextChange(d.key, d.value);
        }
      } catch {}
    });

    this.loadRequests();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
    if (this.contextRefreshTimer) {
      clearTimeout(this.contextRefreshTimer);
    }
  }

  private normalizeContextValue(value: any): string {
    const v = (value ?? "").toString().trim();
    if (!v || v.toLowerCase() == "null" || v.toLowerCase() == "undefined") {
      return "";
    }
    return v;
  }

  private headers() {
    return { Authorization: localStorage.getItem("t") };
  }

  private async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      if (typeof r == "string") return null;
      return r;
    } catch {
      return null;
    }
  }

  private applyContextChange(key: string, value: any) {
    const nextAccountId =
      key === "accountId"
        ? this.normalizeContextValue(value)
        : this.normalizeContextValue(localStorage.getItem("accountId"));
    const nextRegion =
      key === "regionId"
        ? this.normalizeContextValue(value)
        : this.normalizeContextValue(localStorage.getItem("regionId"));

    if (nextAccountId === this.accountId && nextRegion === this.region) return;

    this.accountId = nextAccountId;
    this.region = nextRegion;
    this.rows = [];
    this.error = "";
    this.loading = true;
    this.showLaunchEc2Modal = false;
    this.scheduleContextRefresh();
  }

  private scheduleContextRefresh() {
    if (this.contextRefreshTimer) clearTimeout(this.contextRefreshTimer);
    this.contextRefreshTimer = setTimeout(() => {
      this.contextRefreshTimer = null;
      this.accountId = this.normalizeContextValue(localStorage.getItem("accountId")) || this.accountId;
      this.region = this.normalizeContextValue(localStorage.getItem("regionId")) || this.region;
      this.loadRequests();
    }, 150);
  }

  async loadRequests() {
    const sequence = ++this.loadSequence;
    const accountId = this.accountId;
    const region = this.region;

    this.loading = true;
    this.notifier.loading(true);
    this.error = "";
    this.rows = [];

    if (!accountId || !region) {
      if (sequence === this.loadSequence) {
        this.loading = false;
        this.notifier.loading(false);
        this.error = "Account and region are required.";
      }
      return;
    }

    const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/create_ec2_instance/api/ec2/requests?aws_account_id=${encodeURIComponent(
      accountId
    )}&region=${encodeURIComponent(region)}&limit=50`;
    const r = await this.get(url);
    if (
      this.destroyed ||
      sequence !== this.loadSequence ||
      accountId !== this.accountId ||
      region !== this.region
    ) {
      return;
    }

    this.loading = false;
    this.notifier.loading(false);

    if (!r) {
      this.error = "Unable to load EC2 requests.";
      return;
    }

    this.rows = Array.isArray(r.items) ? r.items : [];
  }

  detailQueryParams(row: any) {
    const params: any = {
      aws_account_id: this.accountId,
      region: row?.region || this.region,
    };
    if (this.preserveServiceCatalogHeader) {
      params.source = "service-catalog";
    }
    return params;
  }

  openLaunchEc2Modal() {
    this.showLaunchEc2Modal = true;
  }

  closeLaunchEc2Modal(_: boolean = false) {
    this.showLaunchEc2Modal = false;
  }
}
